/**
 * Funciones tipadas de los endpoints /races (v15.5-§8.3).
 *
 * Espejo de backend/app/blueprints/races/routes.py.
 *
 * §8.3.C: GET (listar). §8.3.D: POST + PATCH + DELETE. §8.3.E:
 * POST /races/:id/promote.
 */

import { apiFetch } from "./client";
import type {
  CreateRacePayload,
  PromoteRaceResponse,
  RaceResultPayload,
  RaceTarget,
  RaceTargetResponse,
  RacesResponse,
  UpdateRacePayload,
} from "@/types/api";

/**
 * GET /races — lista race_targets del user autenticado.
 *
 * Devuelve `{races: [...]}` con array ORDENADO por race_date asc
 * (backend ya lo ordena). Máximo 5 elementos por la invariante §8.3.
 * Array vacío si el user no tiene ninguna race_target — caller renderiza
 * empty state.
 */
export async function getRaces(): Promise<RacesResponse> {
  return apiFetch<RacesResponse>("/races");
}

/**
 * POST /races — crea race_target nuevo (v15.5-§8.3.D).
 *
 * Errores posibles (ApiError.code):
 * - race_date_required / race_date_invalid / race_date_in_past_or_today
 * - race_distance_required / invalid_distance
 * - surface_required / invalid_surface
 * - beginner_max_5k (gate §8.4)
 * - max_races_reached (>=5 race_targets ya)
 */
export async function createRace(
  payload: CreateRacePayload,
): Promise<RaceTargetResponse> {
  return apiFetch<RaceTargetResponse>("/races", {
    method: "POST",
    body: payload,
  });
}

/**
 * PATCH /races/:id — edita campos de un race_target (v15.5-§8.3.D).
 *
 * Body parcial — solo se aplican los campos presentes. NO acepta
 * `is_primary` (backend rechaza con `cannot_change_is_primary_here`).
 *
 * Errores posibles: mismos que createRace + race_target_not_found (404),
 * race_target_not_owned (403), cannot_change_is_primary_here (defensa).
 */
export async function updateRace(
  raceId: number,
  payload: UpdateRacePayload,
): Promise<RaceTargetResponse> {
  return apiFetch<RaceTargetResponse>(`/races/${raceId}`, {
    method: "PATCH",
    body: payload,
  });
}

/**
 * PATCH /races/:id con campos `actual_*` (v16.3 B.2).
 *
 * Espejo del formulario opcional del día de carrera. Envía SOLO los campos
 * rellenos por el atleta (filtrado por el caller — el componente
 * `RaceResultModal` arma el payload). Si llega cualquier `actual_*`, el
 * backend marca `completed=true` automáticamente.
 *
 * Convierte el wrapper `{race_target}` del backend en `RaceTarget` directo
 * para que el caller maneje el objeto sin desempaquetar.
 *
 * Errores posibles (ApiError.code):
 * - actual_time_invalid, actual_distance_invalid, actual_hr_invalid,
 *   actual_feeling_invalid, actual_weather_invalid (400 — validación
 *   client-side debería prevenirlos, pero defensa server-side igual).
 * - race_target_not_found (404), race_target_not_owned (403).
 */
export async function submitRaceResult(
  raceId: number,
  payload: RaceResultPayload,
): Promise<RaceTarget> {
  const data = await apiFetch<RaceTargetResponse>(`/races/${raceId}`, {
    method: "PATCH",
    body: payload,
  });
  return data.race_target;
}

/**
 * DELETE /races/:id — borra race_target (v15.5-§8.3.D).
 *
 * Bloqueos defensivos backend (§8.3.B, hasta respuesta Alberto §8.2.D):
 * - cannot_delete_primary_with_other_races (400): es primary + hay otras.
 * - cannot_delete_only_primary (400): es la única race_target del user.
 *
 * Otros errores: race_target_not_found (404), race_target_not_owned (403).
 *
 * En éxito: 204 No Content (apiFetch devuelve `undefined`).
 */
export async function deleteRace(raceId: number): Promise<void> {
  return apiFetch<void>(`/races/${raceId}`, { method: "DELETE" });
}

/**
 * POST /races/:id/promote — marca race_target como principal (v15.5-§8.3.E).
 *
 * Backend hace: swap demote→promote del primary anterior + regen del plan
 * activo con los datos del nuevo primary (síncrono, patrón §8.4).
 *
 * IDEMPOTENTE (decisión §8.3.B): si ya es primary, devuelve 200 con
 * `was_already_primary=true` y NO regenera nada (la UI lo trata como
 * info silenciosa).
 *
 * Sin plan activo: el race_target se marca primary igualmente,
 * `plan_regenerated=false`. El frontend muestra mensaje distinto
 * ("crea un plan para empezar").
 *
 * Errores posibles (ApiError.code):
 * - race_target_not_found (404): id no existe.
 * - race_target_not_owned (403): pertenece a otro user.
 * - profile_not_found (404): user sin AthleteProfile (estado raro,
 *   normalmente cubierto por onboarding gate).
 * - plan_regeneration_failed (400): el motor rechazó la regen (e.g.
 *   fecha muy próxima). Backend ya hizo ROLLBACK COMPLETO — el estado
 *   pre-promote sigue intacto (verificado en test_v15_5_8_3_e_plan_*).
 *   El frontend muestra `detail` del error inline sin cerrar el modal.
 */
export async function promoteRace(
  raceId: number,
): Promise<PromoteRaceResponse> {
  return apiFetch<PromoteRaceResponse>(`/races/${raceId}/promote`, {
    method: "POST",
  });
}
